import { useState, useEffect, useCallback, useRef } from '@max/max';
import { useRequest, API_PATH } from './useRequest';
import { useRecommendCityList } from '../utils';
// import {
//   getMockRecommendCities,
//   getMockQueryPlayContent,
// } from '../api/mockData';
// 场景标识
const SCENE = 1; // 1-周边游城市列表推荐
const QUERY_STRATEGY = 'LOCAL_EXPERIENCE';
/**
 * 获取目的地攻略数据的Hook
 * @param isShowCenterCity 是否展示定位/常驻城市（仅 TRAVEL_AROUND_SALE 场景生效）
 * @param ctx 业务引擎上下文，用于发送事件
 * @param sceneType 场景类型
 * @param cityIdList 城市 Id 列表，英文逗号分隔（非 TRAVEL_AROUND_SALE 场景使用）
 * @param cityNameList 城市名称列表，英文逗号分隔，需与 cityIdList 一一对应
 * @returns 数据和加载状态
 */
export function useExperienceData(isShowCenterCity = true, ctx, sceneType, cityIdList, cityNameList) {
    // 新接口：推荐城市接口 (3.9)
    const { request: requestRecommendCities } = useRequest(API_PATH.RECOMMEND_CITIES);
    // 新接口：查询体验内容接口 (3.10)
    const { request: requestQueryPlayContent } = useRequest(API_PATH.QUERY_PLAY_CONTENT);
    const [error, setError] = useState(null);
    const [currentCity, setCurrentCity] = useState(null);
    const [centerCity, setCenterCity] = useState(null);
    const [cities, setCities] = useState([]);
    const [experiences, setExperiences] = useState([]);
    const [moreUrl, setMoreUrl] = useState('');
    const [surroundingType, setSurroundingType] = useState(null);
    const [contentType, setContentType] = useState(null);
    const [currentGuideIndex, setCurrentGuideIndex] = useState(1);
    // 缓存上一次触发的条件，用于用户交互时的去重判断（防止 currentGuideIndex 被重复设置为相同值时重复触发）
    const lastTriggerConditionRef = useRef(null);
    // 标记是否已经初始化过（用于避免初始化时的误触发）
    const hasInitializedRef = useRef(false);
    /**
     * 获取 POI 地标名称
     */
    const getLandmark = useCallback((poiList) => {
        if (Array.isArray(poiList) && poiList.length > 0) {
            return poiList[0].poiName || '';
        }
        return '';
    }, []);
    /**
     * 触发事件的内部函数
     */
    const triggerEvents = useCallback((cityId, index, experienceList) => {
        // 更新缓存的触发条件（用于用户交互时的去重判断）
        lastTriggerConditionRef.current = {
            cityId,
            currentGuideIndex: index
        };
        // 获取体验数据
        const currentExperience = experienceList.length > 0 ? experienceList[index - 1] : null;
        const landmark = getLandmark(currentExperience?.poiInfoList);
        console.log('体验攻略组件事件触发>>>', cityId, index, landmark);
        // 发送事件
        ctx?._communication?.emit?.('travelAroundGuideChange', {
            playName: currentExperience?.playName || '',
            playUnifyIdString: currentExperience?.playUnifyIdString || '',
            productInfoList: currentExperience?.productInfoList || [],
            cityId
        });
        ctx?._communication?.emit?.('dynamicPoiListSearchParamsChanged', {
            landmark,
            cityId,
            lat: currentExperience?.poiInfoList?.[0]?.latitude,
            lng: currentExperience?.poiInfoList?.[0]?.longitude
        });
    }, [ctx, getLandmark]);
    /**
     * 通过配置的 cityIdList + cityNameList 构建城市列表（非 TRAVEL_AROUND_SALE 场景）
     */
    const loadConfigCities = useCallback(() => {
        const ids = (cityIdList || '').split(',').map(s => s.trim()).filter(Boolean);
        const names = (cityNameList || '').split(',').map(s => s.trim());
        const cityList = ids.map((id, index) => ({
            cityId: Number(id),
            cityName: names[index] || id,
            isSelected: false
        })).filter(item => !Number.isNaN(item.cityId) && item.cityId > 0);
        setCities(cityList);
        if (cityList.length > 0 && !currentCity) {
            setCurrentCity(cityList[0]);
        }
    }, [cityIdList, cityNameList]);
    /**
     * 加载推荐城市列表
     */
    const loadRecommendCities = useCallback(async (centerCityId) => {
        try {
            const params = {
                scene: SCENE,
                platform: 1,
                cityId: centerCityId
            };
            const res = await requestRecommendCities(params);
            // const res = getMockRecommendCities(centerCityId)?.data;
            if (res?.uni_status_code) {
                throw new Error('请求数据失败');
            }
            let cityList = res?.cityInfos.map(city => ({
                cityId: city.cityId,
                cityName: city.cityName,
                isSelected: false
            })) || [];
            // 如果需要展示定位/常驻城市，将其放在首位
            if (res?.centerCityId && res?.centerCityName) {
                const centerCityItem = {
                    cityId: res.centerCityId,
                    cityName: res.centerCityName,
                    isSelected: false
                };
                setCenterCity(centerCityItem);
                if (isShowCenterCity) {
                    // 根据 cityId 去重，防止定位城市重复
                    cityList = cityList.filter(city => city.cityId !== res.centerCityId);
                    cityList = [centerCityItem, ...cityList];
                }
                else {
                    cityList = cityList.filter(city => city.cityId !== res.centerCityId);
                }
            }
            setCities(cityList);
            // 保存 surroundingType
            if (res?.surroundingType) {
                setSurroundingType(res.surroundingType);
            }
            // 自动设置第一个城市为当前城市
            if (cityList.length > 0 && !currentCity) {
                setCurrentCity(cityList[0]);
            }
        }
        catch (err) {
            setError(err);
            console.error('[useExperienceData] loadRecommendCities error:', err);
        }
    }, []);
    /**
     * 加载城市体验内容
     */
    const loadPlayContent = useCallback(async (cityId) => {
        let experienceData = [];
        try {
            const params = {
                itemId: cityId.toString(),
                itemType: 2,
                queryStrategy: QUERY_STRATEGY,
                ...(sceneType ? {
                    sceneType
                } : {})
            };
            const res = await requestQueryPlayContent(params);
            // const res = getMockQueryPlayContent(params.itemId)?.data;
            if (res?.uni_status_code) {
                throw new Error('请求数据失败');
            }
            experienceData = res?.contents?.[0]?.cellDetails || [];
            setMoreUrl(res?.contents?.[0]?.jumpUrl || '');
            setExperiences(experienceData);
            setContentType(res?.contentType || null);
        }
        catch (err) {
            setError(err);
            console.error('[useExperienceData] loadPlayContent error:', err);
        }
        finally {
            // 无论接口成功还是失败，都重置 currentGuideIndex 回 1
            // 触发事件，包含去重逻辑，传入获取到的体验数据
            triggerEvents(cityId, 1, experienceData);
            setCurrentGuideIndex(1);
            // 标记已初始化
            hasInitializedRef.current = true;
        }
    }, [triggerEvents, sceneType]);
    // 初始化加载数据
    useEffect(() => {
        if (useRecommendCityList(sceneType)) {
            loadRecommendCities();
        }
        else {
            loadConfigCities();
        }
    }, [sceneType, loadRecommendCities, loadConfigCities]);
    useEffect(() => {
        if (currentCity?.cityId) {
            // 切换城市时重置初始化标志
            hasInitializedRef.current = false;
            loadPlayContent(currentCity.cityId);
        }
    }, [currentCity?.cityId, loadPlayContent]);
    // 监听 currentGuideIndex 变化，触发事件（用户交互导致的索引变化）
    useEffect(() => {
        // 未初始化或无城市时直接返回
        if (!currentCity?.cityId || !hasInitializedRef.current) {
            return;
        }
        // 使用 lastTriggerConditionRef 判断是否真正改变（需要同时检查 cityId 和 currentGuideIndex）
        const lastCondition = lastTriggerConditionRef.current;
        if (lastCondition && lastCondition.cityId === currentCity.cityId && lastCondition.currentGuideIndex === currentGuideIndex) {
            // 条件相同，不需要触发事件（去重）
            return;
        }
        triggerEvents(currentCity.cityId, currentGuideIndex, experiences);
    }, [currentGuideIndex, currentCity?.cityId, experiences, triggerEvents]);
    return {
        error,
        currentCity,
        centerCity,
        cities,
        experiences,
        moreUrl,
        surroundingType,
        contentType,
        currentGuideIndex,
        setCurrentCity,
        setCurrentGuideIndex,
        loadRecommendCities,
        loadPlayContent
    };
}
